import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Chip, Text, useTheme } from '@Sage/hoi-poi-ui';
import { CopilotContext, CopilotSidebar } from '@Sage/ssm-web5';

import useCopilot from 'hooks/useCopilot';
import SageCopilotIcon from 'lib/metadata/icons/SageCopilot';
import { COPILOT_DOMAINS, getCopilotActions, getCopilotFunctions } from 'utils/copilotRegistry';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const COPILOT_SIDEBAR_MENU_ON_CLASS = 'copilot-sidebar--on';
const COPILOT_SIDEBAR_LOCAL_STORAGE_KEY = 'CopilotSidebarOn';

const CopilotSidebarTrigger = () => {
    // ========================================
    // CONTEXT & HOOKS
    // ========================================
    const { getMethodsForCopilot } = useContext(CopilotContext);
    const theme = useTheme();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const userData = useSelector((state) => state.config.userData || {});

    // ========================================
    // LOCAL STATE
    // ========================================
    const [isOpen, setIsOpen] = useState(
        window.localStorage.getItem(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY) !== null,
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const openSidebar = useCallback(() => {
        setIsOpen(true);
    }, []);

    const { copilotActivatedDisclaimer, renderCopilotDisclaimer, setCopilotModalAfterDisclaimer } =
        useCopilot({
            onConfirmDisclaimerSuccess: openSidebar,
        });

    const triggerSidebar = useCallback(() => {
        if (isOpen) {
            // Always allow closing the sidebar
            setIsOpen(false);
        } else if (copilotActivatedDisclaimer) {
            // Disclaimer accepted, open directly
            setIsOpen(true);
        } else {
            // Show disclaimer first
            setCopilotModalAfterDisclaimer(true);
        }
    }, [copilotActivatedDisclaimer, isOpen, setCopilotModalAfterDisclaimer]);

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        getMethodsForCopilot.registerFeatureMethods(
            COPILOT_DOMAINS.SSM,
            getCopilotActions(COPILOT_DOMAINS.SSM),
        );
        getMethodsForCopilot.registerFunctions(getCopilotFunctions());
    }, [getMethodsForCopilot]);

    // Close sidebar if restored from localStorage but disclaimer not accepted
    useEffect(() => {
        if (isOpen && !copilotActivatedDisclaimer) {
            setIsOpen(false);
        }
    }, [copilotActivatedDisclaimer, isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add(COPILOT_SIDEBAR_MENU_ON_CLASS);
            window.localStorage.setItem(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY, true);
        } else {
            document.body.classList.remove(COPILOT_SIDEBAR_MENU_ON_CLASS);
            window.localStorage.removeItem(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY);
        }
        return () => {
            // Only clear sidebar state on unmount if it was closed,
            // so that an intentionally open sidebar can persist via localStorage.
            if (!isOpen) {
                document.body.classList.remove(COPILOT_SIDEBAR_MENU_ON_CLASS);
                window.localStorage.removeItem(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY);
            }
        };
    }, [isOpen]);

    const sidebarProps = useMemo(
        () => ({
            onClose: triggerSidebar,
            userData,
        }),
        [triggerSidebar, userData],
    );

    // ========================================
    // RENDER
    // ========================================
    return (
        <>
            <div className="copilot-sidebar-trigger" onClick={triggerSidebar}>
                <Chip
                    type="text"
                    size="large"
                    className={
                        !isOpen
                            ? 'copilot-sidebar-trigger__button'
                            : 'copilot-sidebar-trigger__button--open'
                    }
                    overrides={{
                        Text: {
                            style: {
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            },
                        },
                    }}
                >
                    <div
                        className={
                            !isOpen
                                ? 'copilot-sidebar-trigger__icon'
                                : 'copilot-sidebar-trigger__icon--open'
                        }
                    >
                        <SageCopilotIcon />
                    </div>
                    <Text color={theme.colors.primary.black}>{getLiteral('label_copilot')}</Text>
                </Chip>
            </div>
            <div
                className={classnames('copilot-sidebar-container', {
                    'copilot-sidebar-container--opened': isOpen,
                })}
            >
                <CopilotSidebar {...sidebarProps} />
            </div>
            {renderCopilotDisclaimer}
        </>
    );
};

export default memo(CopilotSidebarTrigger);
